import type { CanvasElement } from './index';
import type { EnhancedFieldMapping } from './mapping';

export type PreviewMode = 'background' | 'overlay' | 'combined';

export interface PreviewModeConfig {
  key: PreviewMode;
  label: string;
  description: string;
  icon: string;
  shortcut: string;
}

export const PREVIEW_MODES: PreviewModeConfig[] = [
  {
    key: 'background',
    label: 'Background',
    description: 'JPEG output — rasterized visual elements (BACKGROUND_LAYER)',
    icon: 'Image',
    shortcut: '1',
  },
  {
    key: 'overlay',
    label: 'Overlay',
    description: 'JS output — HTML/CSS div overlays with real DOM text (OVERLAY_LAYER)',
    icon: 'Layers',
    shortcut: '2',
  },
  {
    key: 'combined',
    label: 'Combined',
    description: 'Final result — overlay composited on background (what end users see)',
    icon: 'Monitor',
    shortcut: '3',
  },
];

export interface ResolvedElement {
  original: CanvasElement;
  resolved: CanvasElement;
  appliedMappings: AppliedMapping[];
  fullyResolved: boolean;
  errors: string[];
}

export interface AppliedMapping {
  mapping: EnhancedFieldMapping;
  rawValue: string | null;
  resolvedValue: string;
  fromSource: boolean;
}

export type ADACheckSeverity = 'error' | 'warning' | 'info';

export interface ADACheckResult {
  id: string;
  severity: ADACheckSeverity;
  elementId: string | null;
  elementName: string | null;
  rule: string;
  message: string;
  suggestion: string;
}

export const ADA_CHECK_RULES = {
  TEXT_IN_BACKGROUND: {
    rule: 'TEXT_IN_BACKGROUND',
    severity: 'error' as ADACheckSeverity,
    message: 'Text element is in BACKGROUND_LAYER — it will be rasterized and invisible to screen readers',
    suggestion: 'Move this text element to OVERLAY_LAYER so it renders as real DOM text',
  },
  MISSING_ALT_TEXT: {
    rule: 'MISSING_ALT_TEXT',
    severity: 'warning' as ADACheckSeverity,
    message: 'Image element has no alt text',
    suggestion: 'Add descriptive alt text for screen readers, or mark as decorative',
  },
  LOW_CONTRAST: {
    rule: 'LOW_CONTRAST',
    severity: 'warning' as ADACheckSeverity,
    message: 'Text may have insufficient contrast against its background',
    suggestion: 'Ensure text meets WCAG AA contrast ratio of 4.5:1 for normal text or 3:1 for large text',
  },
  BUTTON_NO_HREF: {
    rule: 'BUTTON_NO_HREF',
    severity: 'warning' as ADACheckSeverity,
    message: 'Button element has no link URL',
    suggestion: 'Add a destination URL so the button is functional in the overlay',
  },
  EMPTY_OVERLAY: {
    rule: 'EMPTY_OVERLAY',
    severity: 'info' as ADACheckSeverity,
    message: 'No elements are assigned to OVERLAY_LAYER — the .js overlay file will be empty',
    suggestion: 'Assign text and interactive elements to OVERLAY_LAYER for ADA compliance',
  },
  DYNAMIC_UNRESOLVED: {
    rule: 'DYNAMIC_UNRESOLVED',
    severity: 'warning' as ADACheckSeverity,
    message: 'Dynamic element has unresolved field mappings',
    suggestion: 'Ensure all dynamic fields have valid mappings or fallback values',
  },
  FONT_TOO_SMALL: {
    rule: 'FONT_TOO_SMALL',
    severity: 'warning' as ADACheckSeverity,
    message: 'Text font size is below 12px — may be difficult to read',
    suggestion: 'Increase font size to at least 12px for readability',
  },
} as const;
